/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Bucket } from '../../types';

interface DeleteBucketModalProps {
    isOpen: boolean;
    bucket: Bucket | null;
    onClose: () => void;
    onDelete: () => Promise<void>;
    isDeleting: boolean;
    deleteError?: string | null;
}

const DeleteBucketModal: React.FC<DeleteBucketModalProps> = ({
    isOpen,
    bucket,
    onClose,
    onDelete,
    isDeleting,
    deleteError,
}) => {
    const [confirmationName, setConfirmationName] = useState('');

    // Reset confirmation when modal opens/closes or bucket changes
    useEffect(() => {
        if (!isOpen) {
            setConfirmationName('');
        }
    }, [isOpen, bucket]);

    // Check if confirmation matches bucket name
    const isConfirmed = bucket ? confirmationName === bucket.name : false;

    const handleDelete = async () => {
        if (!isConfirmed) return;
        await onDelete();
    };

    const handleClose = () => {
        if (!isDeleting) {
            onClose();
        }
    };

    if (!isOpen || !bucket) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-2xl shadow-xl w-full max-w-md mx-4 border border-gray-800">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/20 rounded-lg">
                            <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-white">Delete Bucket</h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-300 rounded-lg disabled:opacity-50"
                        disabled={isDeleting}
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    {/* Warning Message */}
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-sm text-red-300">
                            Are you sure you want to delete this bucket?
                        </p>
                        <p className="text-sm text-red-400 mt-2">
                            <strong>This action cannot be undone.</strong> All objects stored in this
                            bucket will be permanently deleted.
                        </p>
                    </div>

                    {/* Bucket Info */}
                    <div className="p-3 bg-gray-800 rounded-lg">
                        <p className="text-sm text-gray-400">
                            Bucket name: <strong className="text-white">{bucket.name}</strong>
                        </p>
                        {bucket.object_count > 0 && (
                            <p className="text-sm text-gray-400 mt-1">
                                Contains: <strong className="text-white">{bucket.object_count} objects</strong>
                            </p>
                        )}
                    </div>

                    {/* API Error Display */}
                    {deleteError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-sm text-red-400">{deleteError}</p>
                        </div>
                    )}

                    {/* Confirmation Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Type <strong>{bucket.name}</strong> to confirm:
                        </label>
                        <input
                            type="text"
                            value={confirmationName}
                            onChange={(e) => setConfirmationName(e.target.value)}
                            className={`input ${confirmationName && !isConfirmed
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                : ''
                                }`}
                            placeholder="Enter bucket name"
                            disabled={isDeleting}
                            autoComplete="off"
                            autoFocus
                        />
                        {confirmationName && !isConfirmed && (
                            <p className="mt-1 text-sm text-red-400">
                                Name doesn't match
                            </p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex gap-3 p-6 border-t border-gray-700">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="btn-secondary flex-1"
                        disabled={isDeleting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={!isConfirmed || isDeleting}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {isDeleting ? 'Deleting...' : 'Delete Bucket'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteBucketModal;
